import type { ChatMessage } from "shared";

/**
 * Typed HTTP client for the server's JSON API. All responses use the
 * `{ success: 0 | 1, ... }` envelope. Paths are relative so the Vite dev proxy
 * (and the production same-origin static host) both work.
 */

type Envelope<T> = ({ success: 1 } & T) | { success: 0; error_message: string };

async function post<T>(path: string, body: unknown): Promise<Envelope<T>> {
  const res = await fetch(path, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  return (await res.json()) as Envelope<T>;
}

async function get<T>(path: string): Promise<Envelope<T>> {
  const res = await fetch(path);
  return (await res.json()) as Envelope<T>;
}

export interface GroupInfo {
  group_id: string;
  codename: string;
  pretty_name: string;
  number_connected: number;
  max_connections: number;
}

/** Chat rows as stored server-side (superset of the broadcast ChatMessage). */
export interface StoredChat {
  id: number;
  group_id: string;
  user_id: string | null;
  name: string | null;
  contents: string | null;
  image_url: string | null;
  system: boolean;
  files: string | null;
  created_at: string;
}

export interface StoredCaption {
  id: number;
  group_id: string;
  contents: string;
  created_at: string;
}

export interface AdminStage {
  segment_id: string;
  desc?: string;
}

/**
 * A previewable puzzle as the server describes it. Deliberately carries no
 * display copy — names and blurbs live in src/preview/config.ts.
 */
export interface PreviewPuzzle {
  key: string;
  theme: string;
  filepath: string | null;
  secondary_filepath: string | null;
}

export const api = {
  groupInfo: (id: string) => get<{ group: GroupInfo }>(`/api/group_info?id=${encodeURIComponent(id)}`),
  getMessages: (group_id: string) => post<{ payload: StoredChat[] }>("/api/get_messages", { group_id }),
  getCaptions: (group_id: string) =>
    post<{ payload: StoredCaption[] }>("/api/get_captions", { group_id }),
  getGroupName: (group_id: string) => post<{ name: string | null }>("/api/get_group_name", { group_id }),
  saveGroupName: (group_id: string, group_name: string) =>
    post<Record<string, never>>("/api/tools/save_group_name", { group_id, group_name }),
  askForClue: (group_id: string) => post<Record<string, never>>("/api/ask_for_clue", { group_id }),
  checkPuzzle: (group_id: string, puzzle_code: string, theme_id: string, answer: string) =>
    post<Record<string, never>>("/api/check_puzzle", { group_id, puzzle_code, theme_id, answer }),
  populatePositionBar: (group_id: string) =>
    post<{ current_key: string; arc: { payload: AdminStage[] } }>(
      "/api/admin_tools/populate_position_bar",
      { group_id },
    ),
  forceToStage: (group_id: string, stage_id: string) =>
    post<Record<string, never>>("/api/admin_tools/force_to_stage", { group_id, stage_id }),
  deleteAllMessages: (group_id: string) =>
    post<Record<string, never>>("/api/admin_tools/delete_all_messages", { group_id }),
  previewPuzzles: () => get<{ puzzles: PreviewPuzzle[] }>("/api/preview/puzzles"),
  previewSetup: (group_id: string, segment_id: string) =>
    post<Record<string, never>>("/api/preview/setup", { group_id, segment_id }),
};

export type { ChatMessage };
